import Dexie, { type EntityTable } from 'dexie';
import { Logger as LogModule } from 'tslog';
import { IS_PROD } from '@/utils/constants';

type LogTransport = Dexie & {
  logs: EntityTable<LogData, 'id'>;
};

export const loggerDb = new Dexie('log-database') as LogTransport;

loggerDb.version(1).stores({
  logs: '++id, timestamp, name, level, [name+timestamp]',
});

export function createLogger<T = unknown>(
  name: string,
): Pick<LogModule<T>, 'fatal' | 'error' | 'warn' | 'info' | 'log' | 'debug' | 'silly' | 'trace'> {
  try {
    const logger = new LogModule<T>({
      name,
      minLevel: IS_PROD ? 4 : 0,
      hideLogPositionForProduction: IS_PROD,
      argumentsArrayName: 'args',
      prettyLogTemplate: '{{logLevelName}}\t[{{name}}]\t{{rawIsoStr}}\t{{fileNameWithLine}}\n',
    });

    logger.attachTransport((logObj) => {
      loggerDb.logs.add({
        name: logObj._meta?.name ?? '',
        level: logObj._meta?.logLevelId ?? 0,
        timestamp: logObj._meta?.date.valueOf() ?? Date.now(),
        content: JSON.stringify(logObj.args),
      });
    });

    return logger;
  } catch (error) {
    console.error(error);
    return {
      debug: (...message: unknown[]) => {
        // biome-ignore lint/suspicious/noConsole: stand-in for tslog
        IS_PROD && console.debug(...message);
        return undefined;
      },
      error: (...message: unknown[]) => {
        console.error(...message);
        return undefined;
      },
      info: (...message: unknown[]) => {
        console.info(...message);
        return undefined;
      },
      log: (...message: unknown[]) => {
        // biome-ignore lint/suspicious/noConsole: stand-in for tslog
        IS_PROD && console.log(...message);
        return undefined;
      },
      silly: (...message: unknown[]) => {
        // biome-ignore lint/suspicious/noConsole: stand-in for tslog
        IS_PROD && console.log(...message);
        return undefined;
      },
      trace: (...message: unknown[]) => {
        console.trace(...message);
        return undefined;
      },
      warn: (...message: unknown[]) => {
        console.warn(...message);
        return undefined;
      },
      fatal: (...message: unknown[]) => {
        console.error(...message);
        return undefined;
      },
    };
  }
}

export async function purgeLogs(retentionTime = 1000 * 60 * 60 * 24 * 3): Promise<number> {
  try {
    return await loggerDb.logs
      .where('timestamp')
      .belowOrEqual(Date.now() - (retentionTime >= Number.MAX_SAFE_INTEGER ? 0 : retentionTime))
      .delete();
  } catch (error) {
    createLogger('Logger').warn('purgeLogs', error);
    return 0;
  }
}

export interface LogData {
  id: number;
  level: number;
  name: string;
  timestamp: number;
  content: string;
}
